<template>
  <div class="form">
    <div class="form__box">
      <div class="form__block">
        <p>{{ t('fields.firstName') }}</p>
        <ui-input
          :type="'text'"
          :placeholder="t('fields.firstName')"
          :rules="[required]"
          v-model="firstName"
        />
      </div>
      <div class="form__block">
        <p>{{ t('fields.lastName') }}</p>
        <ui-input
          :type="'text'"
          :placeholder="t('fields.lastName')"
          :rules="[required]"
          v-model="lastName"
        />
      </div>
    </div>
    <div class="form__block">
      <p>{{ t('fields.email') }}</p>
      <ui-input
        :type="'text'"
        :placeholder="t('fields.email')"
        :rules="[isEmail]"
        v-model="email"
      />
    </div>
    <div class="form__box">
      <div class="form__block">
        <p>{{ t('fields.newPassword') }}</p>
        <ui-input
          :type="'password'"
          :placeholder="t('fields.newPassword')"
          :rules="[isPasswordValid]"
          v-model="newPassword"
        />
      </div>
      <div class="form__block">
        <p>{{ t('fields.newPasswordConfirm') }}</p>
        <ui-input
          :type="'password'"
          :placeholder="t('fields.newPasswordConfirm')"
          :rules="[compareStrings]"
          v-model="passwordConfirm"
        />
      </div>
    </div>
    <ui-button
        class="form__button"
        :isLoading="isLoading"
        @click="updateUser"
    >
      {{ t('buttons.save') }}
    </ui-button>
  </div>
</template>

<script setup>
import {isEmail, isPasswordValid, required} from "@/core/rules/textFieldRules.js";
import UiInput from "@/components/ui/ui-input.vue";
import {useI18n} from "vue-i18n";
import {computed, ref} from "vue";
import {useAuthStore} from "@/stores/auth.js";
import {Auth} from "@/core/api/auth.js";
import {ElNotification} from "element-plus";
import {useRouter} from "vue-router";
import UiButton from "@/components/ui/ui-button.vue";

const {t} = useI18n()
const authStore = useAuthStore()
const router = useRouter()

const accessToken = computed(() => {
  return authStore.accessToken ? authStore.accessToken : ''
})
const userUuid = computed(() => {
  return authStore.user ? authStore.user.uuid : ''
})
const userEmail = computed(() => {
  return authStore.user ? authStore.user.email : ''
})
const userFirstName = computed(() => {
  return authStore.user ? authStore.user.firstName : ''
})
const userLastName = computed(() => {
  return authStore.user ? authStore.user.lastName : ''
})

const firstName = ref(userFirstName.value)
const lastName = ref(userLastName.value)
const email = ref(userEmail.value)
const newPassword = ref('')
const passwordConfirm = ref('')

const isLoading = ref(false)

const compareStrings = (v) => {
  if (v === newPassword.value) return true
  return t('errors.compare')
}

const updateUser = async () => {
  isLoading.value = true

  const res = await Auth.updateUser(
    `uuid: "${userUuid.value}"` +
      (email.value ? `, email: "${email.value}"` : "") +
      (firstName.value ? `, firstName: "${firstName.value}"` : "") +
      (lastName.value ? `, lastName: "${lastName.value}"` : "") +
      (newPassword.value ? `, password: "${newPassword.value}"` : "") +
      (passwordConfirm.value ? `, confirmPassword: "${passwordConfirm.value}"` : ""),
      accessToken.value
  )

  if (!res.errors) {
    if(userEmail.value !== email.value) {
      await logout()

      ElNotification({
        message: t('popup.updateEmail'),
        type: 'info'
      })
    } else {
      authStore.setUser({
        user: res.data.updateUser.user,
        accessToken: accessToken.value
      })

      // if (res.data.updateUser.user.language !== translation.currentLocale) {
      //   translations.switchLanguage(res.data.updateUser.user.language)
      // }

      ElNotification({
        message: t('popup.updateData'),
        type: 'success'
      })
    }
  } else {
    ElNotification({
      message: res.errors[0].message,
      type: 'error'
    })
  }

  isLoading.value = false
}

const logout = async () => {
  authStore.setUser({
    user: null,
    accessToken: null
  })

  localStorage.removeItem('sf-refresh')

  await router.push({
    name: 'home',
    params: {
      locale: localStorage.getItem('fpt-user-locale') || 'en-gb'
    }
  })
}
</script>

<style lang="scss" scoped>
.form {
  width: 585px;
  display: flex;
  flex-direction: column;
  gap: 20px;

  &__box {
    display: flex;
    align-items: flex-start;
    gap: 50px;
  }

  &__block {
    width: 100%;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 20px;

    & p {
      color: #5B194F;
      font-size: 16px;
      font-weight: 600;
    }
  }

  &__button {
    width: fit-content;
    padding-inline: 50px;
  }
}

@media (max-width: 800px) {
  .form {
    width: 100%;
    gap: 30px;

    &__box {
      flex-direction: column;
      gap: 30px;
    }
  }
}
</style>